import React, { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { MdOutlineAccountCircle } from "react-icons/md";
import { CiLogout } from "react-icons/ci";
import { useAuth } from "@/contexts/AuthContext";
import { FaChevronDown } from "react-icons/fa";
import Image from "next/image";
import userDefaults from "@/data/userDefaults.json";

const DropdownUser = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { user, loading } = useAuth();
  const trigger = useRef(null);
  const dropdown = useRef(null);

  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current || !trigger.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      ) {
        return;
      }
      setDropdownOpen(false);
    };

    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [dropdownOpen]);

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };

    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [dropdownOpen]);

  const imgSrc = useMemo(() => {
    if (loading || !user || typeof window === "undefined") return userDefaults.defaultProfile;
    
    let b64 = localStorage.getItem("profile_image");
    
    if (!b64 || b64 === "default") return userDefaults.defaultProfile;

    if (b64.startsWith("data:")) return b64;

    return `data:image/jpeg;base64,${b64}`;
  }, [user, loading]);

  return (
    <div className="relative">
      {user ? (
        <Link
          ref={trigger}
          onClick={(e) => {
            e.preventDefault();
            setDropdownOpen(!dropdownOpen);
          }}
          className="flex items-center gap-4"
          href=""
        >
          <span className="hidden text-right lg:block">
            <span className="block text-sm font-medium text-black dark:text-white">
              {user.nombre}
            </span>
            <span className="block text-xs">{user.cedularuc}</span>
          </span>

          <span className="h-12 w-12 rounded-full">
            {imgSrc ? (
              <Image
                width={112}
                height={112}
                src={imgSrc}
                alt="User"
                className="rounded-full border-4 border-white"
              />
            ) : (
              <div className="flex h-[112px] w-[112px] items-center justify-center rounded-full border-4 border-white">
                <MdOutlineAccountCircle size={40} />
              </div>
            )}
          </span>

          <FaChevronDown />
        </Link>
      ) : (
        <div className="flex flex-row items-center gap-x-4">
          <Link href="/auth/signin" className="hover:text-primary">
            Iniciar Sesión
          </Link>
          <Link href="https://registrociudadano.portoviejo.gob.ec/">
            <button className="btn btn-primary">Crear Cuenta</button>
          </Link>
        </div>
      )}

      <div
        ref={dropdown}
        onFocus={() => setDropdownOpen(true)}
        onBlur={() => setDropdownOpen(false)}
        className={`absolute right-0 mt-4 flex w-62.5 flex-col rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark ${
          dropdownOpen ? "block" : "hidden"
        }`}
      >
        <ul className="flex flex-col gap-5 border-b border-stroke px-6 py-7.5 dark:border-strokedark">
          <li>
            <Link
              href="/profile"
              className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
            >
              <MdOutlineAccountCircle size={25} />
              Mi Perfil
            </Link>
          </li>
        </ul>
        <button
          className="flex items-center gap-3.5 px-6 py-4 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
          onClick={() => {
            window.location.href = "/auth/logout";
          }}
        >
          <CiLogout size={25} />
          Cerrar Sesión
        </button>
      </div>
    </div>
  );
};

export default DropdownUser;